import React, {useState, useEffect, useRef, forwardRef} from 'react';
import { ICONS } from 'Utils/icons';

export const Toc = forwardRef((props, ref) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activeSection, setActiveSection] = useState('');
  const [isTopNavExpanded, setIsTopNavExpanded] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const topNavRef = useRef(null);
  const data = props.data.commonFormatsData;
  const isSideNav = props.data.isSideNav;
  
  useEffect(() => {
    const initialExpanded = new Set();
    data.commonFormatsData.forEach((item, index) => {
      if (item.children && item.children.length) {
        const l1Key = `level1-${index}`;
        initialExpanded.add(l1Key);
        
        item.children.forEach((child, j) => {
          if (child.children && child.children.length) {
            const l2Key = `${l1Key}-level2-${j}`;
            initialExpanded.add(l2Key);
          }
        });
      }
    });
    setExpandedItems(initialExpanded);
  }, [data.commonFormatsData]);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY+60;
      
      let closestSection = '';
      let closestDistance = Infinity;
      
      for (const [key, iref] of Object.entries(ref.current)) {
        if (iref) {
          const distance = Math.abs(iref.offsetTop - scrollPosition);
          if (iref.offsetTop <= scrollPosition && distance < closestDistance) {
            closestDistance = distance;
            closestSection = key;
          }
        }
      }
      
      if (closestSection) {
        setActiveSection(closestSection);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ref]);
  
  useEffect(() => {
    if (!isSideNav && isTopNavExpanded && !isCollapsing) {
      const handleClickOutside = (event) => {
        if (topNavRef.current && !topNavRef.current.contains(event.target)) {
          handleTopNavCollapse();
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSideNav, isTopNavExpanded, isCollapsing]);
  
  const handleTopNavCollapse = () => {
    setIsCollapsing(true);
    setTimeout(() => {
      setIsTopNavExpanded(false);
      setIsCollapsing(false);
    }, 30);
  };
  
  const toggleExpand = (key, e) => {
    e.stopPropagation();
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };
  
  const scrollToSection = (key) => {
    if (!isSideNav) {
      handleTopNavCollapse();
      setTimeout(() => {
        const element = ref.current[key];
        if (element) {
          const topNavHeight = 60;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY + 25 - topNavHeight - 20;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 32);
    } else {
      const element = ref.current[key];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    document.activeElement?.blur();
  };
  
  const getActiveTitle = () => {
    const findTitle = (items, parentKey = '') => {
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const key = `${parentKey}level${parentKey ? items[0].level || 2 : 1}-${index}`;
        
        if (key === activeSection && item.title) {
          return item.title;
        }
        
        if (item.children) {
          const childTitle = findTitle(item.children, key + '-');
          if (childTitle) return childTitle;
        }
      }
      return null;
    };
    
    return findTitle(data.commonFormatsData) || 'Table of Contents';
  };
  
  const renderNavItem = (item, index, level, lastLevel1Item, parentKey = '', parentHasNoTitle = false) => {
    const key = `${parentKey}level${level}-${index}`;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(key);
    const isActive = activeSection === key;
    
    if (level === 1 && !item.title && hasChildren) {
      return item.children.map((child, childIndex) =>
        renderNavItem(child, childIndex, 2, lastLevel1Item, key + '-', true)
      );
    }
    
    let paddingStart = 0;
    if (level === 1) {
      paddingStart = 1;
    } else if (level === 2 && parentHasNoTitle) {
      paddingStart = 1;
    } else if (level === 2) {
      paddingStart = 2;
    } else if (level === 3) {
      paddingStart = 3;
    }
    
    return [
      <div
        key={key}
        className={`pt-2 pb-2 sidetoc-nav-item ps-${paddingStart} ${
          isActive ? 'sidetoc-active-section' : 'sidetoc-inactive-section'
        } ${level === 1 || level === 2 ? 'common-formats-header' : ''}`}
      >
        <div className="d-flex align-items-center justify-content-between">
        <span
          onClick={() => scrollToSection(key)}
          onKeyDown={(e) => (e.key === "Enter" ? scrollToSection(key, e) : ()=>{})}
          style={{ flex: 1, cursor: 'pointer' }}
          tabIndex={0}
          className={"body"}
        >
          {item.title}
        </span>
          {hasChildren && (
            <span
              onClick={(e) => toggleExpand(key, e)}
              onKeyDown={(e) => (e.key === "Enter" ? toggleExpand(key, e) : ()=>{})}
              style={{ cursor: 'pointer' }}
              tabIndex={0}
            >
            {isExpanded ? <ICONS.downArrow size={16} /> : <ICONS.rightArrow size={16} />}
          </span>
          )}
        </div>
      </div>,
      
      ...(hasChildren && isExpanded ? item.children.flatMap((child, childIndex) =>
        renderNavItem(child, childIndex, level + 1, lastLevel1Item, key + '-', false)
      ) : []),
      
      ...(level === 1 && index!==lastLevel1Item-1 ? [<div key={`${key}-border`} className="mt-2 mb-2 common-formats-border-bottom-dashed"></div>] : [])
    ];
  };
  
  // Side Navigation
  if (isSideNav) {
    return (
      <div className="sidetoc-container">
        <h2 className="mb-3 common-formats-header toc-header header4">
          Table of Contents
        </h2>
        <div className="sidetoc-menu pb-4 pe-2 pt-1">
          <div className="d-flex flex-column align-items-start" style={{width:'100%'}}>
            {data.commonFormatsData.map((item, index) => renderNavItem(item, index, 1, data.commonFormatsData.length))}
          </div>
        </div>
      </div>
    );
  }
  
  // Top Navigation
  return (
    <div
      ref={topNavRef}
      className={`toptoc-container ${isTopNavExpanded && !isCollapsing ? 'expanded' : ' '}`}
    >
      <div
        className="d-flex align-items-center common-formats-header toc-header justify-content-between p-2 ps-4"
        style={{ cursor: 'pointer', }}
        onClick={() => {
          if (isTopNavExpanded) {
            handleTopNavCollapse();
          } else {
            setIsTopNavExpanded(true);
          }
        }}
      >
        <span className=" header4 pe-3">
          {getActiveTitle()}
        </span>
        {isTopNavExpanded ? <ICONS.upArrow size={20} /> : <ICONS.downArrow size={20} />}
      </div>
      { isTopNavExpanded && !isCollapsing &&
        <div
          className={`p-3 m-1 toptoc-nav-container ${isTopNavExpanded && !isCollapsing ? "expanded" : " "}`}
        >
          <div className="d-flex flex-column align-items-start" style={{width:'100%'}}>
            {data.commonFormatsData.map((item, index) => renderNavItem(item, index, 1, data.commonFormatsData.length))}
          </div>
        </div>
      }
    </div>
  );
});

// export const Toc = ({ data, ref, isSideNav = true }) => {
//   const [expandedItems, setExpandedItems] = useState(new Set());
//   const [activeSection, setActiveSection] = useState('');
//   const [isTopNavExpanded, setIsTopNavExpanded] = useState(false);
//   const [isCollapsing, setIsCollapsing] = useState(false);
//   const topNavRef = useRef(null);
//
//   useEffect(() => {
//     const initialExpanded = new Set();
//     data.commonFormatsData.forEach((item, index) => {
//       if (item.children) {
//         initialExpanded.add(`level1-${index}`);
//       }
//     });
//     setExpandedItems(initialExpanded);
//   }, [data.commonFormatsData]);
//
//   useEffect(() => {
//     const handleScroll = () => {
//       const scrollPosition = window.scrollY+60;
//
//       let closestSection = '';
//       let closestDistance = Infinity;
//
//       for (const [key, iref] of Object.entries(ref.current)) {
//         if (iref) {
//           const distance = Math.abs(iref.offsetTop - scrollPosition);
//           if (iref.offsetTop <= scrollPosition && distance < closestDistance) {
//             closestDistance = distance;
//             closestSection = key;
//           }
//         }
//       }
//
//       if (closestSection) {
//         setActiveSection(closestSection);
//       }
//     };
//
//     window.addEventListener('scroll', handleScroll);
//     handleScroll();
//
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, [ref]);
//
//   useEffect(() => {
//     if (!isSideNav && isTopNavExpanded && !isCollapsing) {
//       const handleClickOutside = (event) => {
//         if (topNavRef.current && !topNavRef.current.contains(event.target)) {
//           handleTopNavCollapse();
//         }
//       };
//
//       document.addEventListener('mousedown', handleClickOutside);
//       return () => document.removeEventListener('mousedown', handleClickOutside);
//     }
//   }, [isSideNav, isTopNavExpanded, isCollapsing]);
//
//   const handleTopNavCollapse = () => {
//     setIsCollapsing(true);
//     setTimeout(() => {
//       setIsTopNavExpanded(false);
//       setIsCollapsing(false);
//     }, 30);
//   };
//
//   const toggleExpand = (key, e) => {
//     e.stopPropagation();
//     setExpandedItems(prev => {
//       const newSet = new Set(prev);
//       if (newSet.has(key)) {
//         newSet.delete(key);
//       } else {
//         newSet.add(key);
//       }
//       return newSet;
//     });
//   };
//
//   const scrollToSection = (key) => {
//     if (!isSideNav) {
//       handleTopNavCollapse();
//       setTimeout(() => {
//         const element = ref.current[key];
//         if (element) {
//           const topNavHeight = 60;
//           const elementPosition = element.getBoundingClientRect().top;
//           const offsetPosition = elementPosition + window.scrollY + 25 - topNavHeight - 20;
//
//           window.scrollTo({
//             top: offsetPosition,
//             behavior: 'smooth'
//           });
//         }
//       }, 32);
//     } else {
//       const element = ref.current[key];
//       if (element) {
//         element.scrollIntoView({ behavior: 'smooth', block: 'start' });
//       }
//     }
//   };
//
//   const getActiveTitle = () => {
//     const findTitle = (items, parentKey = '') => {
//       for (let index = 0; index < items.length; index++) {
//         const item = items[index];
//         const key = `${parentKey}level${parentKey ? items[0].level || 2 : 1}-${index}`;
//
//         if (key === activeSection && item.title) {
//           return item.title;
//         }
//
//         if (item.children) {
//           const childTitle = findTitle(item.children, key + '-');
//           if (childTitle) return childTitle;
//         }
//       }
//       return null;
//     };
//
//     return findTitle(data.commonFormatsData) || 'Table of Contents';
//   };
//
//   // const renderNavItem = (item, index, level, parentKey = '', parentHasNoTitle = false) => {
//   //   const key = `${parentKey}level${level}-${index}`;
//   //   const hasChildren = item.children && item.children.length > 0;
//   //   const isExpanded = expandedItems.has(key);
//   //   const isActive = activeSection === key;
//   //
//   //   if (level === 1 && !item.title && hasChildren) {
//   //     return (
//   //       <React.Fragment key={key}>
//   //         {item.children.map((child, childIndex) =>
//   //           renderNavItem(child, childIndex, 2, key + '-', true)
//   //         )}
//   //       </React.Fragment>
//   //     );
//   //   }
//   //
//   //   let paddingStart = 0;
//   //   if (level === 1) {
//   //     paddingStart = 1;
//   //   } else if (level === 2 && parentHasNoTitle) {
//   //     paddingStart = 1;
//   //   } else if (level === 2) {
//   //     paddingStart = 2;
//   //   } else if (level === 3) {
//   //     paddingStart = 3;
//   //   }
//   //
//   //   return (
//   //     <React.Fragment key={key}>
//   //         <div
//   //           className={`pt-2 pb-2 sidetoc-nav-item ps-${paddingStart} ${
//   //             isActive ? 'sidetoc-active-section' : 'sidetoc-inactive-section'
//   //           } ${level === 1 || level === 2 ? 'common-formats-header' : ''}`}
//   //         >
//   //           <div className="d-flex align-items-center justify-content-between">
//   //             <span
//   //               onClick={() => scrollToSection(key)}
//   //               style={{ flex: 1, cursor: 'pointer' }}
//   //             >
//   //               {item.title}
//   //             </span>
//   //             {hasChildren && (
//   //               <span
//   //                 onClick={(e) => toggleExpand(key, e)}
//   //                 style={{ cursor: 'pointer' }}
//   //               >
//   //                 {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
//   //               </span>
//   //             )}
//   //           </div>
//   //         </div>
//   //
//   //         {hasChildren && isExpanded && (
//   //           <div className="d-flex flex-column" style={{width:'100%', display: 'block'}}>
//   //             {item.children.map((child, childIndex) =>
//   //               renderNavItem(child, childIndex, level + 1, key + '-', false)
//   //             )}
//   //           </div>
//   //         )}
//   //         {level === 1 && <div className="mt-2 mb-2 common-formats-border-bottom-dashed"></div>}
//   //     </React.Fragment>
//   //   );
//   // };
//   const renderNavItem = (item, index, level, lastLevel1Item, parentKey = '', parentHasNoTitle = false) => {
//     const key = `${parentKey}level${level}-${index}`;
//     const hasChildren = item.children && item.children.length > 0;
//     const isExpanded = expandedItems.has(key);
//     const isActive = activeSection === key;
//
//     if (level === 1 && !item.title && hasChildren) {
//       return item.children.map((child, childIndex) =>
//         renderNavItem(child, childIndex, 2, lastLevel1Item, key + '-', true)
//       );
//     }
//
//     let paddingStart = 0;
//     if (level === 1) {
//       paddingStart = 1;
//     } else if (level === 2 && parentHasNoTitle) {
//       paddingStart = 1;
//     } else if (level === 2) {
//       paddingStart = 2;
//     } else if (level === 3) {
//       paddingStart = 3;
//     }
//
//     return [
//       <div
//         key={key}
//         className={`pt-2 pb-2 sidetoc-nav-item ps-${paddingStart} ${
//           isActive ? 'sidetoc-active-section' : 'sidetoc-inactive-section'
//         } ${level === 1 || level === 2 ? 'common-formats-header' : ''}`}
//       >
//         <div className="d-flex align-items-center justify-content-between">
//         <span
//           onClick={() => scrollToSection(key)}
//           onKeyDown={(e) => (e.key === "Enter" ? scrollToSection(key, e) : ()=>{})}
//           style={{ flex: 1, cursor: 'pointer' }}
//           tabIndex={0}
//         >
//           {item.title}
//         </span>
//           {hasChildren && (
//             <span
//               onClick={(e) => toggleExpand(key, e)}
//               onKeyDown={(e) => (e.key === "Enter" ? toggleExpand(key, e) : ()=>{})}
//               style={{ cursor: 'pointer' }}
//               tabIndex={0}
//             >
//             {isExpanded ? <ICONS.downArrow size={16} /> : <ICONS.rightArrow size={16} />}
//           </span>
//           )}
//         </div>
//       </div>,
//
//       ...(hasChildren && isExpanded ? item.children.flatMap((child, childIndex) =>
//         renderNavItem(child, childIndex, level + 1, lastLevel1Item, key + '-', false)
//       ) : []),
//
//       ...(level === 1 && index!==lastLevel1Item-1 ? [<div key={`${key}-border`} className="mt-2 mb-2 common-formats-border-bottom-dashed"></div>] : [])
//     ];
//   };
//
//   // Side Navigation
//   if (isSideNav) {
//     return (
//       <div className="sidetoc-container">
//         <h5 className="mb-3 common-formats-header toc-header header4">
//           Table of Contents
//         </h5>
//         <div className="sidetoc-menu pb-4 pe-2">
//           <div className="d-flex flex-column align-items-start" style={{width:'100%'}}>
//             {data.commonFormatsData.map((item, index) => renderNavItem(item, index, 1, data.commonFormatsData.length))}
//           </div>
//         </div>
//       </div>
//     );
//   }
//
//   // Top Navigation
//   return (
//     <div
//       ref={topNavRef}
//       className={`toptoc-container ${isTopNavExpanded && !isCollapsing ? 'expanded' : ' '}`}
//     >
//       <div
//         className="d-flex align-items-center common-formats-header toc-header justify-content-between p-2 ps-4"
//         style={{ cursor: 'pointer', }}
//         onClick={() => {
//           if (isTopNavExpanded) {
//             handleTopNavCollapse();
//           } else {
//             setIsTopNavExpanded(true);
//           }
//         }}
//       >
//         <span className=" header4 pe-3">
//           {getActiveTitle()}
//         </span>
//         {isTopNavExpanded ? <ICONS.upArrow size={20} /> : <ICONS.downArrow size={20} />}
//       </div>
//       { isTopNavExpanded && !isCollapsing &&
//         <div
//           className={`p-3 m-1 toptoc-nav-container ${isTopNavExpanded && !isCollapsing ? "expanded" : " "}`}
//         >
//           <div className="d-flex flex-column align-items-start" style={{width:'100%'}}>
//             {data.commonFormatsData.map((item, index) => renderNavItem(item, index, 1, data.commonFormatsData.length))}
//           </div>
//         </div>
//       }
//     </div>
//   );
// };